import React, { useState, useEffect } from 'react';
import { Form, Input, Button, notification, List, Card, Drawer } from 'antd';
import axios from 'axios';
import { API_URL } from '../../apis/apiCotizaciones';
import { API_URL as SERVICIO_API_URL } from '../../apis/apiServicios';

const CrearCotizacion = ({ isDrawerVisible, onClose }) => {
  const [form] = Form.useForm();
  const [productos, setProductos] = useState([]);
  const [servicios, setServicios] = useState([]); // Estado para almacenar servicios

  // Función para cargar productos y servicios desde localStorage
  const cargarProductosYServicios = () => {
    const cotizacionesGuardadas = JSON.parse(localStorage.getItem('cotizaciones')) || [];
    setProductos(cotizacionesGuardadas);

    const serviciosGuardados = JSON.parse(localStorage.getItem('servicios')) || [];
    setServicios(serviciosGuardados); // Cargar servicios
  };

  useEffect(() => {
    // Cargar productos y servicios cuando se abre el Drawer
    if (isDrawerVisible) {
      cargarProductosYServicios();
    }
  }, [isDrawerVisible]); // Escuchar cambios en isDrawerVisible

  const crearServicios = async (servicios) => {
    const promises = servicios.map(async (servicio) => {
        const servicioConEstado = {
            ...servicio,
            estado: 3, // Ajusta esto según sea necesario
        };
        const response = await axios.post(SERVICIO_API_URL, servicioConEstado);
        return response.data.id; // Asumiendo que la respuesta contiene el ID del servicio creado
    });
    return Promise.all(promises); // Devuelve una lista de IDs de servicios creados
  };

  const onFinish = async (values) => {
    try {
      // Verifica si hay al menos un producto o servicio
      if (productos.length === 0 && servicios.length === 0) {
        notification.error({
          message: 'Error al crear la cotización',
          description: 'Debes agregar al menos un producto o un servicio a la cotización.',
        });
        return; // Detener el proceso si no hay productos o servicios
      }

      // Primero, crea los servicios y obtén sus IDs
      const idsServiciosCreados = await crearServicios(servicios);

      // Luego, crea la cotización
      const newCotizacion = {
          nombreCompleto: values.nombreCompleto,
          correo: values.correo,
          telefono: values.telefono,
          productos: productos.map(producto => producto.id),
          servicios: idsServiciosCreados, // Usa los IDs devueltos por la creación de servicios
          estado: 3,
      };

      await axios.post(`${API_URL}`, newCotizacion);
      notification.success({
          message: 'Cotización creada con éxito',
          description: 'Tu cotización ha sido creada y se procesará pronto.',
      });
      form.resetFields();
      localStorage.removeItem('cotizaciones');
      localStorage.removeItem('servicios');
      setProductos([]);
      setServicios([]);
      onClose();
    } catch (error) {
      console.error('Error al crear la cotización:', error.response.data);
      notification.error({
          message: 'Error al crear la cotización',
          description: error.response.data.message || 'Ocurrió un error al intentar crear la cotización. Por favor, inténtalo de nuevo.',
      });
    }
  };

  const eliminarProducto = (productoId) => {
    const nuevosProductos = productos.filter(producto => producto.id !== productoId);
    setProductos(nuevosProductos);
    localStorage.setItem('cotizaciones', JSON.stringify(nuevosProductos));
    notification.info({
      message: 'Producto eliminado',
      description: 'El producto ha sido eliminado de la cotización.',
    });
  };

  const eliminarServicio = (servicioId) => {
    const nuevosServicios = servicios.filter(servicio => servicio.id !== servicioId);
    setServicios(nuevosServicios);
    localStorage.setItem('servicios', JSON.stringify(nuevosServicios));
    notification.info({
      message: 'Servicio eliminado',
      description: 'El servicio ha sido eliminado de la cotización.',
    });
  };

  return (
    <Drawer
      title="Carrito de Cotización"
      placement="right"
      width={400}
      onClose={onClose}
      visible={isDrawerVisible}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          label="Nombre Completo"
          name="nombreCompleto"
          rules={[
            { required: true, message: 'Por favor ingresa tu nombre completo' },
            { pattern: /^[a-zA-Z\s]+$/, message: 'El nombre solo debe contener letras y espacios' }
          ]}
        >
          <Input placeholder="Ej: Juan Perez" />
        </Form.Item>

        <Form.Item
          label="Correo"
          name="correo"
          rules={[
            { required: true, type: 'email', message: 'Por favor ingresa un correo válido' }
          ]}
        >
          <Input placeholder="Ej: example@gmail.com" />
        </Form.Item>

        <Form.Item
          label="Teléfono"
          name="telefono"
          rules={[
            { required: true, message: 'Por favor ingresa tu número de teléfono' },
            { pattern: /^\d{10}$/, message: 'El teléfono debe contener exactamente 10 dígitos' }
          ]}
        >
          <Input placeholder="Ej: 6656879802" />
        </Form.Item>

        {productos.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3>Productos a Cotizar:</h3>
            <List
              grid={{ gutter: 16, column: 1 }}
              dataSource={productos}
              renderItem={producto => (
                <List.Item>
                  <Card
                    title={producto.nombre}
                    actions={[
                      <Button type="link" danger onClick={() => eliminarProducto(producto.id)}>
                        Eliminar
                      </Button>
                    ]}
                  >
                    <p><strong>Número de Serie:</strong> {producto.numeroserie}</p>
                    <p><strong>Peso:</strong> {producto.peso} kg</p>
                    <p><strong>Descripción:</strong> {producto.descripcion}</p>
                  </Card>
                </List.Item>
              )}
            />
          </div>
        )}

        {servicios.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3>Servicios a Cotizar:</h3>
            <List
              grid={{ gutter: 16, column: 1 }}
              dataSource={servicios}
              renderItem={servicio => (
                <List.Item>
                  <Card
                    title={`Servicio: ${servicio.tipo}`} // Asegúrate de mostrar el nombre correcto
                    actions={[
                      <Button type="link" danger onClick={() => eliminarServicio(servicio.id)}>
                        Eliminar
                      </Button>
                    ]}
                  >
                    <p><strong>Descripción:</strong> {servicio.descripcion}</p>
                  </Card>
                </List.Item>
              )}
            />
          </div>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
            Crear Cotización
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default CrearCotizacion;
